import { format, parseISO } from "date-fns";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useBoolean } from "@pdpp/lib-react";
import "../shared/components/DeltaBadge/DeltaBadge.css";
import { useGetDayActuals, useGetWeekActuals, useGetYearActuals, } from "../api/actualsApi";
import { selectedForecastDate$ } from "../state/forecastDate";
import { selectedForecastView$ } from "../state/forecastView";
import { weekStartsOn$ } from "../state/weekStartsOn";
import { forecastNumberFormatter, getDriverIdsByName, isAggregateSubType, mapDriversToMenuItems, mapSubTypesToMenuItems, } from "../utils/forecastDataTransform";
import { formatWeekdayDay, formatYearWeekHeader, getFiscalYearFromDate, getRawValueAt, isHistoricalDate, } from "../utils/forecastDateUtils";
import { createFiscalYearWeekKeyResolver, fiscalWeekKeyFromApiWeek, } from "../utils/forecastTableWeekUtils";
import { getVisibleDayTimeSlots } from "../utils/forecastTimeSlotTransform";
import { getDrillDownTarget } from "../utils/forecastViewUtils";
const CHART_STYLE = {
    BACKGROUND: "rgba(33, 120, 200, 0.7)",
    BORDER: "rgba(33, 120, 200, 1)",
    BORDER_WIDTH: 1,
    LEGEND_PADDING: 32,
    MAX_TICKS: 24,
    ACTUALS_COLOR: "rgba(220, 20, 20, 1)",
};
const CHART_STACKS = {
    FORECAST_BARS: "forecast-bars",
    PUBLISHED_LINE: "published-line",
    ACTUALS_LINE: "actuals-line",
};
const CHART_Y_HEADROOM = 1.08;
const finiteChartValue = (value) => typeof value === "number" && Number.isFinite(value) ? value : 0;
const computeForecastChartYMax = (chartData) => {
    const labelCount = chartData.labels?.length ?? 0;
    const lengths = chartData.datasets.map((d) => Array.isArray(d.data) ? d.data.length : 0);
    const maxLen = Math.max(labelCount, ...lengths, 0);
    if (maxLen === 0)
        return 1;
    const stackSumsByIndex = Array.from({ length: maxLen }, () => 0);
    let maxLine = 0;
    for (const ds of chartData.datasets) {
        const { data } = ds;
        if (!Array.isArray(data))
            continue;
        if (ds.type === "line") {
            for (const v of data) {
                maxLine = Math.max(maxLine, finiteChartValue(v));
            }
            continue;
        }
        const stack = ds.stack;
        if (stack === CHART_STACKS.FORECAST_BARS || stack === undefined) {
            for (let i = 0; i < data.length; i++) {
                stackSumsByIndex[i] += finiteChartValue(data[i]);
            }
        }
    }
    const maxStack = stackSumsByIndex.length > 0 ? Math.max(0, ...stackSumsByIndex) : 0;
    const rawMax = Math.max(maxStack, maxLine);
    if (rawMax <= 0)
        return 1;
    return rawMax * CHART_Y_HEADROOM;
};
const findMatchingDrivers = (drivers, targetKey) => {
    if (!drivers || drivers.length === 0)
        return [];
    return drivers.filter((d) => {
        const sub = d.subType ?? "";
        return `${d.name}::${sub}` === targetKey;
    });
};
const aggregateForecastByKey = (items, getKey) => {
    const result = {};
    for (const item of items) {
        const key = getKey(item);
        if (key == null)
            continue;
        if (!result[key]) {
            result[key] = {
                systemGenerated: 0,
                published: null,
                displayValue: 0,
                draft: 0,
                hasDraft: false,
                hasPublished: false,
            };
        }
        const entry = result[key];
        if (entry) {
            const original = item.originalForecast ?? 0;
            const adjusted = item.adjustedForecast ?? null;
            entry.systemGenerated += original;
            if (adjusted != null) {
                entry.published = (entry.published ?? 0) + adjusted;
                entry.hasPublished = true;
            }
            entry.displayValue += original;
            entry.draft += item.draftForecast ?? 0;
            if (item.draftForecast != null) {
                entry.hasDraft = true;
            }
        }
    }
    return result;
};
const aggregateDriverForecast = (raw, currentView, driverName, timeSlots, calendarStartYear) => {
    const aggregatedSystemGenerated = [];
    const aggregatedPublished = [];
    const aggregatedDisplayValue = [];
    const aggregatedDraft = [];
    const aggregatedHasDrafts = [];
    const defaultEntry = {
        systemGenerated: 0,
        published: null,
        displayValue: 0,
        draft: 0,
        hasDraft: false,
        hasPublished: false,
    };
    const relevantDrivers = raw.drivers.filter((d) => d.name === driverName);
    const aggregated = relevantDrivers.flatMap((d) => d.forecast);
    if (currentView === "day") {
        const byTime = aggregateForecastByKey(aggregated, (item) => item.time);
        for (const slot of timeSlots) {
            const entry = byTime[slot] ?? defaultEntry;
            aggregatedSystemGenerated.push(entry.systemGenerated);
            aggregatedPublished.push(entry.published);
            aggregatedDisplayValue.push(entry.displayValue);
            aggregatedDraft.push(entry.draft);
            aggregatedHasDrafts.push(entry.hasDraft);
        }
    }
    else if (currentView === "week") {
        const byDate = aggregateForecastByKey(aggregated, (item) => item.date);
        for (const slot of timeSlots) {
            const entry = byDate[slot] ?? defaultEntry;
            aggregatedSystemGenerated.push(entry.systemGenerated);
            aggregatedPublished.push(entry.published);
            aggregatedDisplayValue.push(entry.displayValue);
            aggregatedDraft.push(entry.draft);
            aggregatedHasDrafts.push(entry.hasDraft);
        }
    }
    else if (currentView === "year") {
        if (calendarStartYear == null) {
            return {
                systemGenerated: timeSlots.map(() => 0),
                published: timeSlots.map(() => null),
                displayValue: timeSlots.map(() => 0),
                draft: timeSlots.map(() => 0),
                hasDrafts: timeSlots.map(() => false),
            };
        }
        const byWeek = aggregateForecastByKey(aggregated, createFiscalYearWeekKeyResolver(calendarStartYear, weekStartsOn$.value));
        for (const slot of timeSlots) {
            const entry = byWeek[slot] ?? defaultEntry;
            aggregatedSystemGenerated.push(entry.systemGenerated);
            aggregatedPublished.push(entry.published);
            aggregatedDisplayValue.push(entry.displayValue);
            aggregatedDraft.push(entry.draft);
            aggregatedHasDrafts.push(entry.hasDraft);
        }
    }
    return {
        systemGenerated: aggregatedSystemGenerated,
        published: aggregatedPublished,
        displayValue: aggregatedDisplayValue,
        draft: aggregatedDraft,
        hasDrafts: aggregatedHasDrafts,
    };
};
const createStackedDatasets = (displayValues, systemGeneratedValues, publishedValues, draftValues, currentView, timeSlots, labelForecast, labelDraftForecast, hasDrafts) => {
    const barDisplayValues = displayValues.slice();
    const visibleBarValues = barDisplayValues.map((barValue, index) => {
        const isDraft = hasDrafts?.[index] ?? false;
        if (isDraft)
            return 0;
        const draft = draftValues[index] ?? 0;
        return draft !== 0 ? 0 : barValue;
    });
    const visibleDraft = draftValues.map((draft) => draft ?? 0);
    const isWeekView = currentView === "week";
    const baseDataset = {
        type: "bar",
        barPercentage: isWeekView ? 0.72 : 0.8,
        categoryPercentage: isWeekView ? 0.72 : 0.8,
        stack: CHART_STACKS.FORECAST_BARS,
    };
    const originalDataset = {
        ...baseDataset,
        label: labelForecast,
        data: visibleBarValues,
        backgroundColor: "rgba(100, 180, 230, 0.5)",
        borderColor: "rgba(100, 180, 230, 0.5)",
        borderWidth: 1,
        custom: {
            rawValues: barDisplayValues,
            systemGeneratedValues: systemGeneratedValues,
            publishedValues: publishedValues,
            yearWeeks: currentView === "year" ? timeSlots : undefined,
        },
    };
    const draftDataset = {
        ...baseDataset,
        label: labelDraftForecast,
        data: visibleDraft,
        backgroundColor: "rgba(33, 120, 200, 0.8)",
        borderColor: "rgba(33, 120, 200, 0.8)",
        borderWidth: 2,
        custom: {
            rawValues: draftValues,
        },
    };
    return [originalDataset, draftDataset];
};
const createPublishedLineDataset = (data, showPoints, label) => {
    return {
        type: "line",
        label,
        data,
        borderColor: "rgba(220, 20, 20, 1)",
        backgroundColor: "transparent",
        borderWidth: 2,
        borderDash: [4, 4],
        pointRadius: showPoints ? 4 : 0,
        pointBackgroundColor: "rgba(220, 20, 20, 1)",
        pointBorderColor: "rgba(220, 20, 20, 1)",
        pointHoverRadius: showPoints ? 6 : 10,
        pointHoverBackgroundColor: "rgba(220, 20, 20, 1)",
        pointHoverBorderColor: "rgba(220, 20, 20, 1)",
        tension: 0,
        cubicInterpolationMode: "default",
        spanGaps: false,
        yAxisID: "y",
        stack: CHART_STACKS.PUBLISHED_LINE,
        order: 0,
    };
};
const createActualsDataset = (data, showPoints, label) => {
    return {
        type: "line",
        label,
        data,
        borderColor: CHART_STYLE.ACTUALS_COLOR,
        backgroundColor: "transparent",
        borderWidth: 2,
        pointRadius: showPoints ? 4 : 0,
        pointBackgroundColor: CHART_STYLE.ACTUALS_COLOR,
        pointBorderColor: CHART_STYLE.ACTUALS_COLOR,
        pointHoverRadius: showPoints ? 6 : 10,
        pointHoverBackgroundColor: CHART_STYLE.ACTUALS_COLOR,
        pointHoverBorderColor: CHART_STYLE.ACTUALS_COLOR,
        tension: 0,
        cubicInterpolationMode: "default",
        spanGaps: false,
        yAxisID: "y",
        stack: CHART_STACKS.ACTUALS_LINE,
    };
};
const buildYearTooltipTitle = (ctx, weeks) => {
    const index = ctx.dataIndex;
    if (!weeks || index < 0 || index >= weeks.length) {
        return ctx.label ?? "";
    }
    const weekStartStr = weeks[index];
    if (weekStartStr == null)
        return ctx.label ?? "";
    const labels = ctx.chart.data.labels;
    const barLabel = labels && labels[index] != null ? String(labels[index]) : "";
    if (barLabel)
        return barLabel;
    return formatYearWeekHeader(weekStartStr);
};
export const useForecastGraphHandler = (forecastApi, data, currentView) => {
    const { t, i18n } = useTranslation();
    const { driverList, setSelectedDriver } = data;
    const driverNameOptions = useMemo(() => {
        const byName = new Map();
        for (const driver of driverList) {
            if (!byName.has(driver.name)) {
                byName.set(driver.name, driver);
            }
        }
        return Array.from(byName.values());
    }, [driverList]);
    const effectiveDriverName = useMemo(() => {
        const trimmedDriverName = data.selectedDriverName?.trim();
        if (trimmedDriverName != null && trimmedDriverName !== "")
            return trimmedDriverName;
        if (data.selectedDriver?.name != null && data.selectedDriver.name !== "")
            return data.selectedDriver.name;
        return driverNameOptions[0]?.name;
    }, [data.selectedDriverName, data.selectedDriver, driverNameOptions]);
    const subTypeOptions = useMemo(() => {
        const subTypes = driverList.filter((driver) => driver.name === effectiveDriverName);
        const bySubType = new Map();
        for (const driver of subTypes) {
            const displaySubType = driver.displaySubType ?? "";
            const subType = driver.subType ?? "";
            if (!bySubType.has(subType)) {
                bySubType.set(subType, { type: subType, label: displaySubType });
            }
        }
        const options = Array.from(bySubType.values());
        options.sort((a, b) => {
            const aAgg = isAggregateSubType(a.type) === true;
            const bAgg = isAggregateSubType(b.type) === true;
            if (aAgg === bAgg)
                return 0;
            return aAgg ? -1 : 1;
        });
        return options;
    }, [effectiveDriverName, driverList]);
    const effectiveSubType = useMemo(() => {
        const type = data.selectedDriverSubType != null
            ? data.selectedDriverSubType
            : data.selectedDriver?.subType;
        const normalizedType = type == null || type === "" ? undefined : type;
        const selectedOption = normalizedType != null
            ? subTypeOptions.find((option) => option.type === normalizedType)
            : undefined;
        if (selectedOption != null) {
            return { type: selectedOption.type, label: selectedOption.label };
        }
        return {
            type: normalizedType,
            label: data.selectedDriver?.displaySubType != null &&
                data.selectedDriver.displaySubType !== ""
                ? data.selectedDriver.displaySubType
                : normalizedType ?? t("labels.filter"),
        };
    }, [data.selectedDriverSubType, data.selectedDriver, subTypeOptions, t]);
    useEffect(() => {
        if (effectiveDriverName == null ||
            effectiveDriverName === "" ||
            effectiveSubType.type != null ||
            subTypeOptions.length !== 1)
            return;
        const onlySubType = subTypeOptions[0];
        if (onlySubType == null)
            return;
        const matchingDrivers = driverList.filter((driver) => driver.name === effectiveDriverName);
        const selectedDriver = matchingDrivers.find((driver) => driver.subType === onlySubType.type) ??
            matchingDrivers[0];
        if (selectedDriver)
            setSelectedDriver({ ...selectedDriver, subType: onlySubType.type });
    }, [
        effectiveDriverName,
        effectiveSubType,
        subTypeOptions,
        driverList,
        setSelectedDriver,
    ]);
    const [showFilter, , hideFilter, toggleFilter] = useBoolean(false);
    const [showSubTypeFilter, , hideSubTypeFilter, toggleSubTypeFilter] = useBoolean(false);
    const driverMenuItems = useMemo(() => mapDriversToMenuItems(driverNameOptions), [driverNameOptions]);
    const subTypeMenuItems = useMemo(() => mapSubTypesToMenuItems(subTypeOptions), [subTypeOptions]);
    const selectedDriverIds = useMemo(() => effectiveDriverName != null
        ? getDriverIdsByName(driverNameOptions, effectiveDriverName)
        : [], [driverNameOptions, effectiveDriverName]);
    const selectedSubTypeIds = useMemo(() => (effectiveSubType.type != null ? [effectiveSubType.type ?? ""] : []), [effectiveSubType]);
    const driverButtonText = useMemo(() => driverNameOptions.length === 0
        ? t("forecastTab.noDriversAvailable")
        : effectiveDriverName == null || effectiveDriverName === ""
            ? t("forecastTab.selectDriver")
            : driverNameOptions.find((driver) => driver.name === effectiveDriverName)?.displayName ?? "", [driverNameOptions, effectiveDriverName, t]);
    const subTypeButtonText = useMemo(() => effectiveSubType == null ? t("labels.filter") : effectiveSubType.label, [effectiveSubType, t]);
    const onDriverNameChange = useCallback((driverName) => {
        const matchingDrivers = driverList.filter((driver) => driver.name === driverName);
        const uniqueSubTypes = [
            ...new Set(matchingDrivers
                .map((driver) => driver.subType)
                .filter((subType) => subType != null)),
        ];
        if (uniqueSubTypes.length === 1 && matchingDrivers[0]) {
            setSelectedDriver({
                ...matchingDrivers[0],
                subType: uniqueSubTypes[0],
            });
        }
        else {
            const allSubTypeDriver = matchingDrivers.find((driver) => isAggregateSubType(driver.subType));
            if (allSubTypeDriver) {
                setSelectedDriver({ ...allSubTypeDriver });
            }
            else {
                const representativeDriver = driverNameOptions.find((driver) => driver.name === driverName);
                setSelectedDriver({
                    id: representativeDriver?.id ?? driverName,
                    displayName: representativeDriver?.displayName ?? driverName,
                    label: representativeDriver?.label ?? driverName,
                    name: driverName,
                    subType: undefined,
                    displaySubType: undefined,
                    unitOfMeasurement: representativeDriver?.unitOfMeasurement ?? "",
                });
            }
        }
    }, [setSelectedDriver, driverNameOptions, driverList]);
    const onSubTypeChange = useCallback((subType) => {
        if (effectiveDriverName == null || effectiveDriverName === "")
            return;
        if (subType == null) {
            const representativeDriver = driverNameOptions.find((driver) => driver.name === effectiveDriverName);
            setSelectedDriver({
                id: representativeDriver?.id ?? effectiveDriverName,
                label: representativeDriver?.label ?? effectiveDriverName,
                name: effectiveDriverName,
                displayName: representativeDriver?.displayName ?? effectiveDriverName,
                displaySubType: undefined,
                unitOfMeasurement: representativeDriver?.unitOfMeasurement ?? "",
                subType: undefined,
            });
        }
        else {
            const matchingDrivers = driverList.filter((driver) => driver.name === effectiveDriverName);
            const matchingDriver = matchingDrivers.find((driver) => driver.subType === subType) ??
                matchingDrivers[0];
            if (matchingDriver)
                setSelectedDriver({ ...matchingDriver, subType });
        }
    }, [effectiveDriverName, setSelectedDriver, driverNameOptions, driverList]);
    const handleDriverSelect = useCallback((ids) => {
        if (ids[0] == null || ids[0] === "")
            return;
        const driver = driverNameOptions.find((option) => option.id === ids[0]);
        if (driver) {
            onDriverNameChange(driver.name);
            hideFilter();
        }
    }, [driverNameOptions, hideFilter, onDriverNameChange]);
    const handleSubTypeSelect = useCallback((ids) => {
        const selectedId = ids[0];
        if (selectedId == null || selectedId === "")
            return;
        onSubTypeChange(selectedId);
        hideSubTypeFilter();
    }, [hideSubTypeFilter, onSubTypeChange]);
    const handleDriverClick = (driverId) => handleDriverSelect([driverId]);
    const handleSubTypeClick = (subType) => handleSubTypeSelect([subType]);
    const handleDriverFilterToggle = useCallback(() => {
        toggleFilter();
        if (showSubTypeFilter)
            hideSubTypeFilter();
    }, [toggleFilter, showSubTypeFilter, hideSubTypeFilter]);
    const handleSubTypeFilterToggle = useCallback(() => {
        toggleSubTypeFilter();
        if (showFilter)
            hideFilter();
    }, [toggleSubTypeFilter, showFilter, hideFilter]);
    const selectedDriver = data.selectedDriver;
    const selectedDate = selectedForecastDate$.value;
    const numericUnitId = data.unitId;
    const selectedDriverName = selectedDriver?.name ?? "";
    const selectedDriverSubType = selectedDriver?.subType ?? "";
    const shouldFetchDayActuals = currentView === "day" && selectedDriverName !== "" && numericUnitId != null;
    const dayActuals = useGetDayActuals(forecastApi, {
        unitId: numericUnitId,
        date: selectedDate,
        driverName: selectedDriverName,
        driverSubType: selectedDriverSubType,
    }, undefined, shouldFetchDayActuals);
    const shouldFetchWeekActuals = currentView === "week" &&
        selectedDriverName !== "" &&
        numericUnitId != null;
    const weekActuals = useGetWeekActuals(forecastApi, {
        unitId: numericUnitId,
        weekStartDate: data.weekStartDate ?? selectedDate,
        driverName: selectedDriverName,
        driverSubType: selectedDriverSubType,
    }, undefined, shouldFetchWeekActuals);
    const fiscalYear = getFiscalYearFromDate(selectedDate);
    const shouldFetchYearActuals = currentView === "year" &&
        selectedDriverName !== "" &&
        numericUnitId != null;
    const yearActuals = useGetYearActuals(forecastApi, {
        unitId: numericUnitId,
        fiscalYear,
        driverName: selectedDriverName,
        driverSubType: selectedDriverSubType,
    }, undefined, shouldFetchYearActuals);
    const { data: rawActualsData } = (() => {
        if (currentView === undefined || selectedDriverName === "") {
            return { data: undefined };
        }
        if (currentView === "day")
            return dayActuals;
        if (currentView === "week")
            return weekActuals;
        if (currentView === "year")
            return yearActuals;
        return { data: undefined };
    })();
    const timeSlotsForChart = useMemo(() => {
        if (data.currentView === "day") {
            return getVisibleDayTimeSlots(data.timeSlots, data.filteredData);
        }
        return data.chartTimeSlots ?? data.timeSlots;
    }, [
        data.currentView,
        data.timeSlots,
        data.filteredData,
        data.chartTimeSlots,
    ]);
    const chartData = useMemo(() => {
        const raw = data.rawForecastData;
        const driver = data.selectedDriver;
        const calendarStartYear = currentView === "year" && raw != null && "year" in raw
            ? raw.year
            : undefined;
        const formatLabels = (slots) => currentView === "week"
            ? slots.map(formatWeekdayDay)
            : currentView === "year"
                ? slots.map((s) => formatYearWeekHeader(s))
                : slots;
        if (!raw || currentView === undefined || driver === undefined) {
            return {
                labels: formatLabels(timeSlotsForChart),
                datasets: data.filteredData.map((row) => ({
                    label: t("labels.forecasted"),
                    data: timeSlotsForChart.map((slot) => row.timeSlots[slot] ?? 0),
                    backgroundColor: CHART_STYLE.BACKGROUND,
                    borderColor: CHART_STYLE.BORDER,
                    borderWidth: CHART_STYLE.BORDER_WIDTH,
                })),
            };
        }
        const showAll = data.selectedDriverSubType == null ||
            data.selectedDriverSubType.trim() === "" ||
            isAggregateSubType(data.selectedDriverSubType) === true;
        if (showAll === true) {
            const dName = effectiveDriverName ?? "";
            const allDrivers = raw.drivers.filter((d) => d.name === dName && isAggregateSubType(d.subType));
            const keyGetter = currentView === "day"
                ? (item) => item.time ?? null
                : currentView === "week"
                    ? (item) => item.date ?? null
                    : calendarStartYear != null
                        ? createFiscalYearWeekKeyResolver(calendarStartYear, weekStartsOn$.value)
                        : () => null;
            let systemGenerated;
            let published;
            let displayValue;
            let draft;
            let drafts;
            if (allDrivers.length > 0) {
                const aggregated = allDrivers.flatMap((d) => d.forecast);
                const byKey = aggregateForecastByKey(aggregated, keyGetter);
                systemGenerated = timeSlotsForChart.map((slot) => byKey[slot]?.systemGenerated ?? 0);
                published = timeSlotsForChart.map((slot) => {
                    const entry = byKey[slot];
                    return entry?.hasPublished === true ? entry.published : null;
                });
                displayValue = timeSlotsForChart.map((slot) => byKey[slot]?.displayValue ?? 0);
                draft = timeSlotsForChart.map((slot) => byKey[slot]?.draft ?? 0);
                drafts = timeSlotsForChart.map((slot) => byKey[slot]?.hasDraft ?? false);
            }
            else {
                const fallback = aggregateDriverForecast(raw, currentView, effectiveDriverName ?? "", timeSlotsForChart, calendarStartYear);
                systemGenerated = fallback.systemGenerated;
                published = fallback.published;
                displayValue = fallback.displayValue;
                draft = fallback.draft;
                drafts = fallback.hasDrafts;
            }
            const [originalDataset, draftDataset] = createStackedDatasets(displayValue, systemGenerated, published, draft, currentView, timeSlotsForChart, t("labels.forecasted"), t("labels.draftForecast"), drafts);
            const hasPublishedValues = published.some((val) => val !== null);
            const publishedLineDataset = hasPublishedValues
                ? createPublishedLineDataset(published, currentView !== "day", t("labels.published"))
                : null;
            const showAllDatasets = [];
            if (publishedLineDataset)
                showAllDatasets.push(publishedLineDataset);
            const labelsAll = formatLabels(timeSlotsForChart);
            const actualsDatasetAll = (() => {
                if (rawActualsData !== undefined && driver !== undefined) {
                    const actualsSelectedKey = `${driver.name}::${driver.subType ?? ""}`;
                    const actualsValuesAll = [];
                    let actualsCutoffIndexAll = labelsAll.length;
                    if (currentView === "day") {
                        const actualsResponse = rawActualsData;
                        const dayMatchingDrivers = findMatchingDrivers(actualsResponse.drivers, actualsSelectedKey);
                        const dayDriver = dayMatchingDrivers[0];
                        if (dayDriver !== undefined) {
                            const byTime = {};
                            for (const interval of dayDriver.actual) {
                                const time = interval.time;
                                if (time !== undefined && time !== "") {
                                    byTime[time] = (byTime[time] ?? 0) + interval.actual;
                                }
                            }
                            const actualsDate = actualsResponse.date;
                            const isDateHistorical = isHistoricalDate(actualsDate);
                            for (let i = 0; i < labelsAll.length; i++) {
                                const slot = timeSlotsForChart[i];
                                if (slot !== undefined && slot !== "") {
                                    const value = byTime[slot] ?? null;
                                    actualsValuesAll.push(isDateHistorical ? value ?? 0 : null);
                                    if (!isDateHistorical &&
                                        actualsCutoffIndexAll === labelsAll.length) {
                                        actualsCutoffIndexAll = i;
                                    }
                                }
                            }
                        }
                    }
                    else if (currentView === "week") {
                        const actualsResponse = rawActualsData;
                        const weekMatchingDrivers = findMatchingDrivers(actualsResponse.drivers, actualsSelectedKey);
                        const weekDriver = weekMatchingDrivers[0];
                        if (weekDriver !== undefined) {
                            const byDate = {};
                            for (const day of weekDriver.actual) {
                                const date = day.date;
                                if (date !== undefined && date !== "") {
                                    byDate[date] = (byDate[date] ?? 0) + day.actual;
                                }
                            }
                            for (let i = 0; i < labelsAll.length; i++) {
                                const slot = timeSlotsForChart[i];
                                if (slot !== undefined && slot !== "") {
                                    const isHistorical = isHistoricalDate(slot);
                                    const value = byDate[slot] ?? null;
                                    actualsValuesAll.push(isHistorical ? value ?? 0 : null);
                                    if (!isHistorical &&
                                        actualsCutoffIndexAll === labelsAll.length) {
                                        actualsCutoffIndexAll = i;
                                    }
                                }
                            }
                        }
                    }
                    else if (currentView === "year") {
                        const actualsResponse = rawActualsData;
                        const yearMatchingDrivers = findMatchingDrivers(actualsResponse.drivers, actualsSelectedKey);
                        const yearDriver = yearMatchingDrivers[0];
                        if (yearDriver !== undefined) {
                            const byWeek = {};
                            const actualsYear = actualsResponse.year;
                            for (const week of yearDriver.actual) {
                                const normalized = fiscalWeekKeyFromApiWeek(week.weekStartDate, week.weekEndDate, actualsYear, weekStartsOn$.value);
                                if (normalized !== "") {
                                    byWeek[normalized] = (byWeek[normalized] ?? 0) + week.actual;
                                }
                            }
                            for (let i = 0; i < labelsAll.length; i++) {
                                const slot = timeSlotsForChart[i];
                                if (slot !== undefined && slot !== "") {
                                    const isHistorical = isHistoricalDate(slot);
                                    const value = byWeek[slot] ?? null;
                                    actualsValuesAll.push(isHistorical ? value ?? 0 : null);
                                    if (!isHistorical &&
                                        actualsCutoffIndexAll === labelsAll.length) {
                                        actualsCutoffIndexAll = i;
                                    }
                                }
                            }
                        }
                    }
                    const filteredActualsValuesAll = actualsValuesAll.map((val, idx) => idx >= actualsCutoffIndexAll ? null : val);
                    return createActualsDataset(filteredActualsValuesAll, currentView !== "day", t("labels.actuals"));
                }
                return null;
            })();
            if (actualsDatasetAll)
                showAllDatasets.push(actualsDatasetAll);
            showAllDatasets.push(draftDataset);
            showAllDatasets.push(originalDataset);
            return {
                labels: labelsAll,
                datasets: showAllDatasets,
            };
        }
        const selectedKey = `${driver.name}::${driver.subType ?? ""}`;
        const matchingDrivers = findMatchingDrivers(raw.drivers, selectedKey);
        if (!matchingDrivers.length)
            return { labels: [], datasets: [] };
        const aggregated = matchingDrivers.flatMap((d) => d.forecast);
        const keyGetter = currentView === "day"
            ? (item) => item.time ?? null
            : currentView === "week"
                ? (item) => item.date ?? null
                : calendarStartYear != null
                    ? createFiscalYearWeekKeyResolver(calendarStartYear, weekStartsOn$.value)
                    : () => null;
        const byKey = aggregateForecastByKey(aggregated, keyGetter);
        const displayValues = timeSlotsForChart.map((slot) => byKey[slot]?.displayValue ?? 0);
        const systemGeneratedValues = timeSlotsForChart.map((slot) => byKey[slot]?.systemGenerated ?? 0);
        const publishedValues = timeSlotsForChart.map((slot) => {
            const entry = byKey[slot];
            return entry?.hasPublished === true ? entry.published : null;
        });
        const draftValues = timeSlotsForChart.map((slot) => byKey[slot]?.draft ?? 0);
        const draftFlags = data.timeSlots.map((slot) => byKey[slot]?.hasDraft ?? false);
        const [originalDataset, draftDataset] = createStackedDatasets(displayValues, systemGeneratedValues, publishedValues, draftValues, currentView, timeSlotsForChart, t("labels.forecasted"), t("labels.draftForecast"), draftFlags);
        const hasPublishedValues = publishedValues.some((val) => val !== null);
        const publishedLineDataset = hasPublishedValues
            ? createPublishedLineDataset(publishedValues, currentView !== "day", t("labels.published"))
            : null;
        const labels = formatLabels(timeSlotsForChart);
        const actualsValues = [];
        let actualsCutoffIndex = labels.length;
        if (rawActualsData !== undefined) {
            const actualsSelectedKey = `${driver.name}::${driver.subType ?? ""}`;
            if (currentView === "day") {
                const actualsResponse = rawActualsData;
                const dayMatchingDrivers = findMatchingDrivers(actualsResponse.drivers, actualsSelectedKey);
                const dayDriver = dayMatchingDrivers[0];
                if (dayDriver !== undefined) {
                    const byTime = {};
                    for (const interval of dayDriver.actual) {
                        const time = interval.time;
                        if (time !== undefined && time !== "") {
                            byTime[time] = (byTime[time] ?? 0) + interval.actual;
                        }
                    }
                    const actualsDate = actualsResponse.date;
                    const isDateHistorical = isHistoricalDate(actualsDate);
                    for (let i = 0; i < labels.length; i++) {
                        const slot = timeSlotsForChart[i];
                        if (slot !== undefined && slot !== "") {
                            const value = byTime[slot] ?? null;
                            actualsValues.push(isDateHistorical ? value ?? 0 : null);
                            if (!isDateHistorical && actualsCutoffIndex === labels.length) {
                                actualsCutoffIndex = i;
                            }
                        }
                    }
                }
            }
            else if (currentView === "week") {
                const actualsResponse = rawActualsData;
                const weekMatchingDrivers = findMatchingDrivers(actualsResponse.drivers, actualsSelectedKey);
                const weekDriver = weekMatchingDrivers[0];
                if (weekDriver !== undefined) {
                    const byDate = {};
                    for (const day of weekDriver.actual) {
                        const date = day.date;
                        if (date !== undefined && date !== "") {
                            byDate[date] = (byDate[date] ?? 0) + day.actual;
                        }
                    }
                    for (let i = 0; i < labels.length; i++) {
                        const slot = timeSlotsForChart[i];
                        if (slot !== undefined && slot !== "") {
                            const isHistorical = isHistoricalDate(slot);
                            const value = byDate[slot] ?? null;
                            actualsValues.push(isHistorical ? value ?? 0 : null);
                            if (!isHistorical && actualsCutoffIndex === labels.length) {
                                actualsCutoffIndex = i;
                            }
                        }
                    }
                }
            }
            else if (currentView === "year") {
                const actualsResponse = rawActualsData;
                const yearMatchingDrivers = findMatchingDrivers(actualsResponse.drivers, actualsSelectedKey);
                const yearDriver = yearMatchingDrivers[0];
                if (yearDriver !== undefined) {
                    const byWeek = {};
                    const actualsYear = actualsResponse.year;
                    for (const week of yearDriver.actual) {
                        const normalized = fiscalWeekKeyFromApiWeek(week.weekStartDate, week.weekEndDate, actualsYear, weekStartsOn$.value);
                        if (normalized !== "") {
                            byWeek[normalized] = (byWeek[normalized] ?? 0) + week.actual;
                        }
                    }
                    for (let i = 0; i < labels.length; i++) {
                        const slot = timeSlotsForChart[i];
                        if (slot !== undefined && slot !== "") {
                            const isHistorical = isHistoricalDate(slot);
                            const value = byWeek[slot] ?? null;
                            actualsValues.push(isHistorical ? value ?? 0 : null);
                            if (!isHistorical && actualsCutoffIndex === labels.length) {
                                actualsCutoffIndex = i;
                            }
                        }
                    }
                }
            }
        }
        const filteredActualsValues = actualsValues.map((val, idx) => idx >= actualsCutoffIndex ? null : val);
        const actualsDataset = createActualsDataset(filteredActualsValues, currentView !== "day", t("labels.actuals"));
        const finalDatasets = [];
        if (publishedLineDataset)
            finalDatasets.push(publishedLineDataset);
        finalDatasets.push(actualsDataset);
        finalDatasets.push(draftDataset);
        finalDatasets.push(originalDataset);
        return {
            labels,
            datasets: finalDatasets,
        };
    }, [
        data.rawForecastData,
        data.timeSlots,
        timeSlotsForChart,
        data.filteredData,
        data.selectedDriver,
        currentView,
        data.selectedDriverSubType,
        effectiveDriverName,
        t,
        rawActualsData,
    ]);
    const chartOptions = useMemo(() => {
        const getTooltipTitle = (items) => {
            if (!items.length)
                return "";
            const ctx = items[0];
            if (!ctx)
                return "";
            if (currentView === "day") {
                const timeLabel = ctx.label;
                if (!timeLabel || !selectedForecastDate$.value)
                    return ctx.label ?? "";
                const base = parseISO(`${selectedForecastDate$.value}T${timeLabel}:00`);
                const end = new Date(base.getTime() + 15 * 60 * 1000);
                const fullDate = base.toLocaleDateString(i18n.language, {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                });
                return `${format(base, "HH:mm")}-${format(end, "HH:mm")} ${fullDate}`;
            }
            if (currentView === "week") {
                const slotDate = timeSlotsForChart[ctx.dataIndex];
                if (slotDate == null || slotDate === "")
                    return ctx.label ?? "";
                const parsedDate = parseISO(slotDate);
                return parsedDate.toLocaleDateString(i18n.language, {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                });
            }
            if (currentView === "year") {
                return buildYearTooltipTitle(ctx, timeSlotsForChart);
            }
            return ctx.label ?? "";
        };
        const buildTooltipPairs = (items) => {
            if (items.length === 0)
                return [];
            const firstItem = items[0];
            if (!firstItem)
                return [];
            const index = firstItem.dataIndex;
            const allDs = firstItem.chart.data.datasets;
            const forecastDs = allDs.find((d) => d.label === t("labels.forecasted"));
            const draftDs = allDs.find((d) => d.label === t("labels.draftForecast"));
            const actualsDs = allDs.find((d) => d.label === t("labels.actuals"));
            const originalLabel = t("labels.systemGenerated");
            const publishedLabel = t("labels.published");
            const draftLabel = draftDs?.label ?? "";
            const actualsLabel = actualsDs?.label ?? "";
            const systemGeneratedRaw = getRawValueAt(forecastDs?.custom?.systemGeneratedValues, index);
            const systemGeneratedVal = forecastNumberFormatter.format(systemGeneratedRaw);
            const pairs = [{ label: originalLabel, value: systemGeneratedVal }];
            const publishedValRaw = forecastDs?.custom?.publishedValues?.[index] ?? null;
            if (publishedValRaw !== null) {
                const publishedVal = forecastNumberFormatter.format(publishedValRaw);
                pairs.push({ label: publishedLabel, value: publishedVal });
            }
            if (draftDs) {
                const draftValRaw = getRawValueAt(draftDs.custom?.rawValues, index);
                const systemGeneratedForPct = getRawValueAt(forecastDs?.custom?.systemGeneratedValues, index);
                const systemGeneratedNum = Number(systemGeneratedForPct);
                const draftNum = Number(draftValRaw);
                const hasDraft = Number.isFinite(systemGeneratedNum) &&
                    Number.isFinite(draftNum) &&
                    draftNum !== 0 &&
                    draftNum !== systemGeneratedNum;
                if (hasDraft) {
                    const draftVal = forecastNumberFormatter.format(draftValRaw);
                    const pctDelta = systemGeneratedNum !== 0
                        ? (draftNum - systemGeneratedNum) / systemGeneratedNum
                        : null;
                    const pctRounded = pctDelta != null ? Math.round(Math.abs(pctDelta * 100)) : 0;
                    const pctSign = pctDelta != null && pctDelta > 0 ? "+" : "-";
                    const pctBadge = pctRounded > 0 ? `${pctSign}${pctRounded}%` : "";
                    const badgeVariant = pctDelta == null || pctRounded === 0
                        ? undefined
                        : pctDelta > 0
                            ? "positive"
                            : pctDelta < 0
                                ? "negative"
                                : "neutral";
                    pairs.push({
                        label: draftLabel,
                        value: draftVal,
                        badge: pctBadge !== "" ? pctBadge : undefined,
                        badgeVariant,
                    });
                }
            }
            const actualsValRaw = actualsDs?.data?.[index] ?? null;
            if (actualsValRaw !== null) {
                const actualsVal = forecastNumberFormatter.format(actualsValRaw);
                pairs.push({ label: actualsLabel, value: actualsVal });
            }
            return pairs;
        };
        const getOrCreateExternalTooltip = (chart) => {
            const parent = chart.canvas.parentNode;
            if (!parent) {
                return document.createElement("div");
            }
            const existing = parent.querySelector(".forecast-graph__external-tooltip");
            if (existing)
                return existing;
            const el = document.createElement("div");
            el.className = "forecast-graph__external-tooltip";
            el.style.position = "absolute";
            el.style.pointerEvents = "none";
            el.style.opacity = "0";
            el.style.background = "rgba(0, 0, 0, 0.92)";
            el.style.border = "1px solid rgba(255, 255, 255, 0.18)";
            el.style.boxShadow = "0 8px 24px rgba(0,0,0,0.35)";
            el.style.borderRadius = "8px";
            el.style.padding = "8px 10px";
            el.style.zIndex = "10";
            el.style.fontFamily = '"Noto IKEA", "Noto Sans", Roboto, sans-serif';
            el.style.fontSize = "12px";
            el.style.color = "rgba(255, 255, 255, 0.95)";
            el.style.lineHeight = "1.35";
            el.style.fontVariantNumeric = "tabular-nums";
            el.style.transition =
                "left 0.12s ease-out, top 0.12s ease-out, opacity 0.18s ease-out";
            parent.style.position = parent.style.position || "relative";
            parent.appendChild(el);
            return el;
        };
        const yAxisMax = computeForecastChartYMax(chartData);
        return {
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: 0 },
            plugins: {
                legend: { display: false },
                tooltip: {
                    enabled: false,
                    external: (context) => {
                        const { chart, tooltip } = context;
                        const el = getOrCreateExternalTooltip(chart);
                        if (tooltip.opacity === 0) {
                            el.style.opacity = "0";
                            return;
                        }
                        const title = getTooltipTitle(tooltip.dataPoints);
                        const pairs = buildTooltipPairs(tooltip.dataPoints);
                        el.innerHTML = "";
                        if (title !== "") {
                            const titleEl = document.createElement("div");
                            titleEl.textContent = title;
                            titleEl.style.fontWeight = "600";
                            titleEl.style.marginBottom = pairs.length > 0 ? "6px" : "0";
                            el.appendChild(titleEl);
                        }
                        if (pairs.length > 0) {
                            const body = document.createElement("div");
                            body.style.display = "grid";
                            body.style.gridTemplateColumns = "1fr auto auto";
                            body.style.columnGap = "8px";
                            body.style.rowGap = "4px";
                            body.style.minWidth = "180px";
                            pairs.forEach((p) => {
                                const labelEl = document.createElement("div");
                                labelEl.textContent = p.label;
                                labelEl.style.justifySelf = "start";
                                labelEl.style.whiteSpace = "nowrap";
                                const badgeEl = document.createElement("div");
                                badgeEl.style.justifySelf = "end";
                                badgeEl.style.whiteSpace = "nowrap";
                                badgeEl.style.display = "flex";
                                badgeEl.style.justifyContent = "flex-end";
                                badgeEl.style.opacity =
                                    p.badge != null && p.badge !== "" ? "1" : "0";
                                if (p.badge != null && p.badge !== "") {
                                    const pill = document.createElement("span");
                                    const variant = p.badgeVariant ??
                                        (p.badge.trim().startsWith("+")
                                            ? "positive"
                                            : p.badge.trim().startsWith("-")
                                                ? "negative"
                                                : "neutral");
                                    pill.className = `delta-badge delta-badge--small ${variant === "positive"
                                        ? "delta-badge--positive"
                                        : variant === "negative"
                                            ? "delta-badge--negative"
                                            : "delta-badge--neutral"}`;
                                    pill.textContent = p.badge;
                                    badgeEl.appendChild(pill);
                                }
                                const valueEl = document.createElement("div");
                                valueEl.textContent = p.value;
                                valueEl.style.justifySelf = "end";
                                valueEl.style.whiteSpace = "nowrap";
                                body.appendChild(labelEl);
                                body.appendChild(badgeEl);
                                body.appendChild(valueEl);
                            });
                            el.appendChild(body);
                        }
                        const { offsetLeft: canvasLeft, offsetTop: canvasTop } = chart.canvas;
                        const { offsetWidth: tooltipWidth, offsetHeight: tooltipHeight } = el;
                        const padding = 10;
                        const tooltipLeft = canvasLeft + tooltip.caretX;
                        const tooltipTop = canvasTop + tooltip.caretY;
                        const canvasRight = canvasLeft + chart.canvas.width / window.devicePixelRatio;
                        const horizontalTransform = tooltipLeft - tooltipWidth / 2 < canvasLeft + padding
                            ? "0%"
                            : tooltipLeft + tooltipWidth / 2 > canvasRight - padding
                                ? "-100%"
                                : "-50%";
                        const verticalTransform = tooltipTop - tooltipHeight * 1.1 < canvasTop + padding
                            ? `${Math.max(-((tooltipTop - canvasTop - padding) / tooltipHeight) * 100, -105)}%`
                            : "-110%";
                        el.style.opacity = "1";
                        el.style.left = `${tooltipLeft}px`;
                        el.style.top = `${tooltipTop}px`;
                        el.style.transform = `translate(${horizontalTransform}, ${verticalTransform})`;
                    },
                },
            },
            onClick: (_event, elements) => {
                if (currentView === undefined || !elements.length)
                    return;
                const timeSlot = timeSlotsForChart[elements[0]?.index ?? -1];
                if (timeSlot == null || timeSlot === "")
                    return;
                const drillDownTarget = getDrillDownTarget(currentView, timeSlot);
                if (drillDownTarget) {
                    selectedForecastView$.value = drillDownTarget.targetView;
                    selectedForecastDate$.value = drillDownTarget.targetDate;
                }
            },
            scales: {
                x: {
                    title: { display: false },
                    ticks: {
                        maxRotation: currentView === "year" ? 45 : 0,
                        minRotation: currentView === "year" ? 45 : 0,
                        autoSkip: true,
                        maxTicksLimit: currentView === "year" ? 60 : CHART_STYLE.MAX_TICKS,
                        callback: function (value, index) {
                            const numericIndex = typeof index === "number"
                                ? index
                                : typeof value === "number"
                                    ? value
                                    : Number(value);
                            const labelsArray = chartData.labels;
                            const defaultLabel = labelsArray?.[numericIndex] != null
                                ? String(labelsArray[numericIndex])
                                : String(value);
                            if (currentView === "day")
                                return String(defaultLabel).endsWith(":00") ? defaultLabel : "";
                            if (currentView === "week")
                                return defaultLabel;
                            if (currentView === "year") {
                                const weekStartStr = timeSlotsForChart[numericIndex];
                                return weekStartStr != null && weekStartStr !== ""
                                    ? formatYearWeekHeader(weekStartStr)
                                    : defaultLabel;
                            }
                            return defaultLabel;
                        },
                    },
                    stacked: true,
                    grid: { display: false },
                },
                y: {
                    min: 0,
                    max: yAxisMax,
                    title: { display: false },
                    stacked: true,
                    grid: { display: false },
                },
            },
        };
    }, [currentView, timeSlotsForChart, chartData, i18n, t]);
    const accuracy = useMemo(() => {
        const raw = data.rawForecastData;
        const driver = data.selectedDriver;
        if (raw == null || driver == null)
            return null;
        const selectedKey = `${driver.name}::${driver.subType ?? ""}`;
        const matchingDrivers = findMatchingDrivers(raw.drivers, selectedKey);
        return matchingDrivers[0]?.accuracy ?? null;
    }, [data.rawForecastData, data.selectedDriver]);
    return {
        forecastApi,
        isLoading: data.isLoading,
        selectedDriver: data.selectedDriver,
        chartData: chartData,
        chartOptions,
        view: currentView,
        unitId: data.unitId,
        hasValidDriverName: data.hasValidDriverName,
        weekStartDate: data.weekStartDate,
        accuracy,
        generatedAt: data.generatedAt,
        filterState: {
            showFilter,
            showSubTypeFilter,
            selectedDriverIds,
            selectedSubTypeIds,
            driverButtonText,
            subTypeButtonText,
        },
        filterHandlers: {
            hideFilter,
            hideSubTypeFilter,
            handleDriverFilterToggle,
            handleSubTypeFilterToggle,
            handleDriverSelect,
            handleDriverClick,
            handleSubTypeSelect,
            handleSubTypeClick,
        },
        filterData: {
            driverMenuItems,
            subTypeMenuItems,
        },
    };
};
//# sourceMappingURL=useForecastGraphHandler.js.map